import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Popover, PopoverBody, Badge, Label } from "reactstrap";
import CheckboxTree from "react-checkbox-tree";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import "./react-checkbox-tree.scss";

// Theme style
import "./lightTheme.scss";

const ButtonCheckboxTree = function ({
  t,
  data,
  onChange,
  loadingData,
  name,
  defaultValue,
  filterItemIcon,
  filterItemTitle,
  filterItemExtraStyle,
  infoGroups,
}) {
  const { mainTheme } = useSelector((state) => state.Layout);
  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [nodesChildren, setNodesChildren] = useState({});

  const toggle = () => setPopoverOpen(!popoverOpen);

  function removeItem(item) {
    setChecked(checked.filter((i) => i !== item));
    setPopoverOpen(false);
  }

  const getChildIdsRecursive = useCallback((obj, key = "uuid") => {
    const findValuesHelper = (groups, list) => {
      if (!groups) return list;
      if (groups instanceof Array) {
        for (const i in groups) {
          list = list.concat(findValuesHelper(groups[i], []));
        }
        return list;
      }
      if (groups[key]) list.push(groups[key]);
      if (typeof groups == "object" && groups !== null) {
        let children = Object.keys(groups);
        if (children.length > 0) {
          for (let i = 0; i < children.length; i++) {
            list = list.concat(findValuesHelper(groups[children[i]], []));
          }
        }
      }
      return list;
    };

    return findValuesHelper(obj, []);
  }, []);

  const groupChildRecursive = useCallback(
    (group) => {
      if (!group) return;

      return group.map((item) => {
        if (item.children) {
          const children = groupChildRecursive(item.children);
          setNodesChildren((prev) => ({
            ...prev,
            [item.uuid]: [
              ...children.map((c) => c.uuid),
              ...getChildIdsRecursive(children),
            ],
          }));
          return {
            ...item,
            value: item.uuid,
            children,
          };
        }
        return {
          ...item,
          value: item.uuid,
        };
      });
    },
    [getChildIdsRecursive]
  );

  const nodes = useMemo(() => {
    if (!data.length) return;

    return groupChildRecursive(data);
  }, [data, groupChildRecursive]);

  const maskIdNumber = useCallback((groups, objGroupsID = {}) => {
    if (groups.length === 0 && groups.uuid) return {};

    groups?.forEach((group) => {
      objGroupsID = {
        ...objGroupsID,
        [group["uuid"]]: { uuid: group["uuid"], label: group["label"] },
      };
      objGroupsID = {
        ...objGroupsID,
        ...maskIdNumber(group["children"] || [], objGroupsID),
      };
    });

    return objGroupsID;
  }, []);

  const groupObject = useMemo(() => {
    if (!nodes?.length) return {};

    return maskIdNumber(nodes);
  }, [nodes, maskIdNumber]);

  const arrayIds = useMemo(() => {
    infoGroups([...Object.values(groupObject)]);
    return Object.keys(groupObject);
  }, [groupObject]);

  const onCheck = (cks, target) => {
    if (target.value === 1) {
      if (target.checked) {
        setChecked([1, ...arrayIds]);
      } else {
        setChecked([]);
      }

      return;
    }

    if (checked?.includes(target.value)) {
      const childrenIds = nodesChildren[target.value] || [];
      cks = cks.filter((c) => !childrenIds.includes(c));
    } else {
      if (nodesChildren.hasOwnProperty(target.value)) {
        const childrenIds = nodesChildren[target.value];
        cks = [...cks, ...childrenIds];
      }
    }

    const aux = [...new Set(cks)].filter((c) => c !== "1");

    if (aux.length === arrayIds?.length - 1) {
      setChecked([1, ...aux]);
    } else {
      setChecked([...aux]);
    }
  };

  useEffect(() => {
    if (defaultValue?.length >= 0) {
      setChecked(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (checked.length === 0 || checked.includes(1)) {
      onChange(name, []);
      return;
    }
    onChange(name, [
      ...new Set(
        checked?.filter((id) => id !== 1)?.map((id) => groupObject[id])
      ),
    ]);
  }, [checked]);

  return (
    <div className={`checkbox-tree ${mainTheme === "dark" ? "dark" : "light"} ${filterItemExtraStyle}`}>
      <Label className="font-size-13">
        {filterItemIcon && (
          <i className={`${filterItemIcon} font-size-13 mr-2`} />
        )}
        {filterItemTitle}
      </Label>
      <Button
        id="Popover1"
        type="button"
        className={"d-flex py-0 button"}
        color="secondary"
      >
        <div className="overflow-auto align-self-center multi-container">
          {data.length === 0 && (
            <div
              className="not-field select2-selection__placeholder 
              css-1wa3eu0-placeholder"
            >
              {t("Choose")}...
            </div>
          )}
          {data.length > 0 && (checked.length === 0 || checked.includes(1)) && (
            <BadgeCustom label={t("All groups")} />
          )}
          {!checked.includes(1) &&
            checked
              ?.filter((id) => groupObject[id]?.label)
              ?.map((id, key) => (
                <BadgeCustom
                  key={key}
                  multi
                  label={groupObject[id]?.label}
                  removeItem={() => removeItem(id)}
                />
              ))}
        </div>
        <i className="d-flex align-self-center icon-arrow mdi mdi-chevron-down font-size-24" />
      </Button>
      <Popover
        placement="bottom"
        isOpen={popoverOpen}
        className={`popover-checkbox-tree ${mainTheme === "dark" ? "dark" : "light"}`}
        target="Popover1"
        toggle={toggle}
        trigger="legacy"
      >
        <PopoverBody>
          {data.length === 0 ? (
            <div
              className="not-field font-size-13 
                select2-selection__placeholder
                css-1wa3eu0-placeholder"
            >
              {t("No matching records found")}
            </div>
          ) : (
            <CheckboxTree
              checked={checked}
              expanded={expanded}
              iconsClass="fa5"
              nodes={nodes}
              showExpandAll={true}
              noCascade
              checkModel={"leaf"}
              id="tree-container"
              showNodeIcon={false}
              onCheck={onCheck}
              onExpand={(el) => {
                setExpanded(el);
              }}
            />
          )}
        </PopoverBody>
      </Popover>
    </div>
  );
};

export default withTranslation()(ButtonCheckboxTree);

const BadgeCustom = ({ label, multi, removeItem }) => (
  <Badge
    className={"multi-value font-size-10 "}
    color={multi ? "light" : "secondary"}
  >
    <div className={"container"}>{label}</div>
    {multi && (
      <Button
        outline
        color="danger"
        className="button-close"
        onClick={removeItem}
      >
        <i
          className={`icon-close mdi mdi-close font-size-12`}
          style={{ fontSize: 15 }}
        />
      </Button>
    )}
  </Badge>
);
